import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', source: '', search: '', paymentStatus: '' });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const navigate = useNavigate();

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
    fetchSubmissions();
  }, [token, navigate]);

  useEffect(() => {
    fetchSubmissions();
  }, [filter, pagination.page]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 10,
        ...(filter.status && { status: filter.status }),
        ...(filter.source && { source: filter.source }),
        ...(filter.search && { search: filter.search }),
        ...(filter.paymentStatus && { paymentStatus: filter.paymentStatus })
      });

      const res = await fetch(`${API_BASE_URL}/api/admin/submissions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      console.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/submissions/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        fetchSubmissions();
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to update status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  const statusColors = {
    new: 'bg-blue-500',
    contacted: 'bg-yellow-500',
    converted: 'bg-green-500'
  };

  const paymentStatusColors = {
    paid: 'bg-green-500 text-white',
    pay_later: 'bg-yellow-500 text-black',
    free_review: 'bg-purple-500 text-white',
    pending: 'bg-gray-500 text-white'
  };

  const paymentStatusLabels = {
    paid: '✅ Paid',
    pay_later: '⏳ Pay Later',
    free_review: '🆓 Free',
    pending: '⏸ Pending'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <nav className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">ProfileWise Admin</h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="p-6">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-gray-400 text-sm">Total Leads</h3>
              <p className="text-3xl font-bold mt-2">{stats.total}</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-gray-400 text-sm">New Today</h3>
              <p className="text-3xl font-bold mt-2 text-blue-400">{stats.todayCount}</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-gray-400 text-sm">Conversion Rate</h3>
              <p className="text-3xl font-bold mt-2 text-green-400">{stats.conversionRate}%</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-gray-400 text-sm">By Status</h3>
              <div className="flex gap-4 mt-2 text-sm">
                <span className="text-blue-400">New: {stats.byStatus.new}</span>
                <span className="text-yellow-400">Contacted: {stats.byStatus.contacted}</span>
                <span className="text-green-400">Converted: {stats.byStatus.converted}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-4 border-b border-gray-700">
            <div className="flex flex-wrap gap-4">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
              />
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
              </select>
              <select
                value={filter.source}
                onChange={(e) => setFilter(prev => ({ ...prev, source: e.target.value }))}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none"
              >
                <option value="">All Sources</option>
                <option value="contact_form">Contact Form</option>
                <option value="landing_modal">Landing Modal</option>
              </select>
              <select
                value={filter.paymentStatus || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, paymentStatus: e.target.value }))}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none"
              >
                <option value="">All Payments</option>
                <option value="paid">✅ Paid</option>
                <option value="pay_later">⏳ Pay Later</option>
                <option value="free_review">🆓 Free Review</option>
                <option value="pending">⏸ Pending</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">Name</th>
                  <th className="px-4 py-3 text-left text-sm">Email</th>
                  <th className="px-4 py-3 text-left text-sm">Phone</th>
                  <th className="px-4 py-3 text-left text-sm">Payment</th>
                  <th className="px-4 py-3 text-left text-sm">Plan</th>
                  <th className="px-4 py-3 text-left text-sm">Amount</th>
                  <th className="px-4 py-3 text-left text-sm">Source</th>
                  <th className="px-4 py-3 text-left text-sm">Status</th>
                  <th className="px-4 py-3 text-left text-sm">Date</th>
                  <th className="px-4 py-3 text-left text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  submissions.map((sub) => (
                    <tr key={sub._id} className="border-t border-gray-700 hover:bg-gray-700/50">
                      <td className="px-4 py-3">{sub.name || '-'}</td>
                      <td className="px-4 py-3">{sub.email}</td>
                      <td className="px-4 py-3">{sub.phone || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${paymentStatusColors[sub.paymentStatus] || 'bg-gray-500'}`}>
                          {paymentStatusLabels[sub.paymentStatus] || sub.paymentStatus || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {sub.plan ? (
                          <span className="text-xs px-2 py-1 bg-indigo-600 rounded">
                            {sub.plan}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {sub.amount > 0 ? (
                          <span className="font-semibold text-green-400">₹{sub.amount}</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-gray-600 rounded">
                          {sub.source === 'contact_form' ? 'Contact' : 'Modal'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${statusColors[sub.status]}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(sub.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={sub.status}
                          onChange={(e) => updateStatus(sub._id, e.target.value)}
                          className="text-sm px-2 py-1 bg-gray-600 border border-gray-500 rounded"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="p-4 border-t border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-400">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
